// src/components/Product/MediaItem.js (نسخه نهایی با دکمه پلی در سمت چپ و دکمه چکیده در سمت راست)

import React from 'react';
import { PlayCircle, Download, Loader, Lock, PauseCircle } from 'lucide-react'; 
import { Link } from 'react-router-dom';
import { MEDIA_BASE_URL } from '../../config'; 

import { usePlayerContext } from '../../context/PlayerContext'; 
import { useAuthContext } from '../../context/AuthContext'; 

import { convertToPersianNumbers } from '../../utils/PersianNumberUtils'; 

// 💡 تابع کمکی برای تبدیل ثانیه به فرمت دقیقه:ثانیه (بدون تغییر)
const formatDuration = (sec) => {
    if (!sec || typeof sec !== 'number' || sec < 0) return convertToPersianNumbers(0) + ' ثانیه'; 
    
    const minutes = Math.floor(sec / 60);
    const seconds = Math.floor(sec % 60);
    
    const persianMinutes = convertToPersianNumbers(minutes); 
    const persianSeconds = convertToPersianNumbers(seconds); 

    const minutePart = minutes > 0 ? `${persianMinutes} دقیقه` : '';
    const secondPart = seconds > 0 ? `${persianSeconds} ثانیه` : '';

    if (minutePart && secondPart) {
        return `${minutePart} و ${secondPart}`;
    }
    return minutePart || secondPart || convertToPersianNumbers(0) + ' ثانیه'; 
};

// 🌟 تابع حیاتی برای ساخت URL کامل از آدرس نسبی (بدون تغییر)
const getAbsoluteMediaUrl = (relativeUrl, baseUrl) => {
    if (!relativeUrl) return null;
    
    if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
        return relativeUrl;
    }

    let finalBaseUrl = baseUrl;
    if (finalBaseUrl && finalBaseUrl.endsWith('/')) {
        finalBaseUrl = finalBaseUrl.slice(0, -1);
    }
    
    let finalRelativeUrl = relativeUrl;
    while (finalRelativeUrl.startsWith('/')) {
        finalRelativeUrl = finalRelativeUrl.substring(1);
    }
    
    return `${finalBaseUrl}/${finalRelativeUrl}`;
};


// 🛑 دریافت onPlay به عنوان prop
const MediaItem = ({ media, productHasAccess, onPlay }) => { 
    
    const { 
        currentMedia, 
        isPlaying, 
        togglePlayPause,
        isLoading // اگر این استیت در PlayerContext وجود دارد
    } = usePlayerContext(); 

    const { isLoggedIn, openAuthModal } = useAuthContext(); 
    
    // 💡 URL مطلق برای مقایسه با currentMedia.url (که URL مطلق/امن است)
    const absoluteMediaUrl = getAbsoluteMediaUrl(media.media_url, MEDIA_BASE_URL); 
    
    // وضعیت پخش: اگر Context، مدیایی با همان URL مطلق/پایه را دارد
    // 🛑 FIX: isThisMediaPlaying باید بر اساس URL مطلق باشد
    const isThisMediaPlaying = currentMedia?.url && currentMedia.url.includes(absoluteMediaUrl) && isPlaying; 
    const isThisMediaSelected = currentMedia?.url && currentMedia.url.includes(absoluteMediaUrl);
    
    // ----------------------------------------------------------------------------------
    
    const handlePlayClick = (e) => {
        e.preventDefault();
        
        const isLocked = media.is_locked && !productHasAccess;
        
        // 🛑 منطق احراز هویت: قفل یا دانلود نیاز به لاگین دارد
        const requiresAuth = isLocked || media.is_downloadable; 

        if (requiresAuth && !isLoggedIn) {
            openAuthModal(); 
            return;
        }

        if (isLocked) {
            alert('برای دسترسی به این محتوای قفل شده، ابتدا درسگفتار را خریداری کنید.');
            return;
        }
        
        // 🛑 FIX VITAL: مدیریت پخش/توقف برای صوتی که در حال پخش است
        // اگر همین مدیا صوتی انتخاب شده است، فقط وضعیت پخش آن را تغییر بده
        if (media.media_type === 'AUDIO' && isThisMediaSelected) {
            togglePlayPause();
            return;
        }
        
        // 🛑 FIX VITAL: برای مدیا جدید (ویدیو، صوت جدید، یا PDF)، رویداد را به والد منتقل کن
        if (onPlay) {
            // ارسال آیتم مدیا به MediaFileList (یا ProductDetailPage) برای تصمیم‌گیری امنیتی
            onPlay(media); 
        }
        
        // در غیر این صورت (فایل غیرقابل پخش)، چیزی رخ نمی‌دهد
    };

    // ----------------------------------------------------------------------------------
    
    const isLocked = media.is_locked && !productHasAccess;
    const hasSummary = media.summary && media.summary.trim() !== '';
    const buttonColor = 'oklch(44.8% 0.119 151.328)'; // رنگ سبز مورد نظر
    
    const rowClasses = `
        flex items-center justify-between py-4 w-full mx-auto transition border-b border-gray-200 
        ${!productHasAccess ? 'cursor-pointer opacity-85' : 'cursor-pointer hover:bg-gray-50'}
        ${isThisMediaSelected ? 'bg-gray-100' : ''}  
    `;
    
    // 💡 کلاس‌های آیکون طوسی روشن
    const iconBaseClasses = "text-gray-400 drop-shadow-sm transition hover:text-gray-600";
  
    return (
        <div 
            className={rowClasses} 
            onClick={handlePlayClick}
        >
        
        <div className="flex items-center gap-4 flex-grow px-4"> 
            
            {/* ========= سمت چپ: دکمه پخش و دانلود ========= */}
            <div className="flex items-center gap-2 flex-shrink-0">
                {/* دکمه پخش/مکث/قفل */}
                <div className="flex items-center justify-center">
                    {isLocked ? (
                        <Lock size={28} className={iconBaseClasses} />
                    ) : (isLoading && isThisMediaSelected) ? (
                        <Loader size={28} className="text-gray-400 animate-spin" />
                    ) : isThisMediaPlaying && media.media_type === 'AUDIO' ? ( 
                        <PauseCircle size={28} className={iconBaseClasses} />
                    ) : (
                        <PlayCircle size={28} className={iconBaseClasses} />
                    )}
                </div>
                {/* دکمه دانلود (اختیاری) */}
                {media.is_downloadable && productHasAccess && (
                    <button 
                        className="p-2 rounded-full hover:bg-gray-100 transition"
                        title="دانلود فایل"
                        onClick={(e) => { e.stopPropagation(); /* منطق دانلود در اینجا */ }}
                    >
                        <Download className="text-gray-800 hover:text-gray-600" size={20} /> 
                    </button>
                )}
            </div>

            {/* ========= بخش مرکزی: عنوان، مدت زمان و نوع فایل ========= */}
            <div className="flex flex-col gap-1 flex-grow">
                <p className={`text-[11px] font-normal ${isThisMediaSelected ? 'text-gray-700' : 'text-gray-500'}`}>
                    {media.title}
                </p> 
                
                <div className="flex items-center gap-3 mt-1">
                    
                    {isLocked && (
                        <span className="flex items-center">
                            <Lock size={16} className="text-red-400 ml-1" />
                        </span>
                    )}
                    
                    {media.duration && (
                        <span className="text-[11px] text-gray-400 font-medium">
                            {formatDuration(media.duration)}
                        </span>
                    )}
                    
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full 
                        ${media.media_type === 'VIDEO' ? 'bg-blue-100 text-blue-800' : 
                          media.media_type === 'AUDIO' ? 'bg-purple-100 text-purple-800' : 
                          'bg-green-100 text-green-800'}`}>
                        {media.media_type === 'VIDEO' ? 'ویدیو' : media.media_type === 'AUDIO' ? 'صوت' : 'PDF'}
                    </span>
                </div>
                
                {isThisMediaPlaying && media.media_type === 'AUDIO' && ( 
                    <span className="text-xs font-bold text-gray-800 mt-1">
                        در حال پخش
                    </span>
                )}
            </div>
            
            {/* ========= سمت راست: دکمه چکیده (مستطیلی) ========= */}
            {hasSummary ? (
                <Link
                    to={`/media-summary/${media.id}`}
                    className="px-3 py-1.5 rounded-xl text-white text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0"
                    style={{ backgroundColor: buttonColor }}
                    title="مشاهده چکیده فایل"
                    onClick={(e) => e.stopPropagation()}
                >
                    چکیده فایل
                </Link>
            ) : (
                <div className="px-3 py-1.5 rounded-xl bg-gray-200 text-gray-400 text-xs cursor-not-allowed whitespace-nowrap flex-shrink-0">
                    بدون چکیده
                </div>
            )}
            
        </div>
        
        </div>
    );
};
export default MediaItem;